// Rail de l'aperçu du dossier : parties, équipe, finances.
// Colonne droite ≥ xl, masquée en dessous.
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Avatar } from '../ui/Avatar';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, canAccessFeature } from '../../services/auth/permissions';
import { useDossierContacts } from '../../hooks/queries/useContacts';
import { useDossierMembers } from '../../hooks/queries/useDossiers';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useTimeEntries } from '../../hooks/queries/useTimeEntries';
import { useBillingDocuments } from '../../hooks/queries/useBillingDocuments';
import { computeFinancialSummary } from '../../services/billing/billingService';
import { formatDuration } from '../../services/timeTracking/timeTrackingService';
import { getContactDisplayName, type Contact, type DossierContactRole } from '../../services/contacts/contactService';
import type { Dossier } from '../../services/dossiers/dossierService';
import { euros, unbilledAmount } from './DossierOverviewBlocks';
import { DossierMembersTab } from './DossierMembersTab';

interface Props {
  dossier: Dossier;
  canManageMembers: boolean;
}

export function DossierSummaryRail({ dossier, canManageMembers }: Props) {
  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role);
  const canSeeBilling = canAccessFeature(role, 'billing_view');

  const { data: links } = useDossierContacts(dossier.id);
  const { data: members } = useDossierMembers(dossier.id);
  const { data: collaborators } = useCollaborators();
  // useTimeEntries / useBillingDocuments n'exposent pas d'option `enabled` : on
  // les appelle toujours filtrés sur ce dossier (jamais `{}`, qui chargerait
  // tout le cabinet) et on masque simplement la carte Financier si l'utilisateur
  // n'a pas la permission — les RLS protègent déjà les données côté serveur.
  const { data: entries } = useTimeEntries({ dossier_id: dossier.id });
  const { data: billingDocs } = useBillingDocuments({ dossier_id: dossier.id });

  const [membersOpen, setMembersOpen] = useState(false);

  const uninvoiced = useMemo(() => {
    const list = (entries ?? []).filter((e) => e.is_billable && !e.invoiced_in_id);
    const minutes = list.reduce((acc, e) => acc + e.duration_minutes, 0);
    return { minutes };
  }, [entries]);
  const toBill = useMemo(() => unbilledAmount(entries ?? []), [entries]);

  const financial = useMemo(() => computeFinancialSummary(billingDocs ?? []), [billingDocs]);

  const orderedMembers = useMemo(
    () =>
      [...(members ?? [])].sort((a, b) =>
        a.role === 'lead_counsel' ? -1 : b.role === 'lead_counsel' ? 1 : 0,
      ),
    [members],
  );

  const sides = useMemo(() => {
    const names = (roles: DossierContactRole[]) =>
      (links ?? [])
        .filter((l) => l.contact && roles.includes(l.role as DossierContactRole))
        .map((l) => getContactDisplayName(l.contact as Contact));
    return [
      {
        key: 'ours',
        title: 'Clients',
        quality: dossier.client_procedural_role,
        main: names(['client']),
        counsel: names(['local_counsel']),
      },
      {
        key: 'theirs',
        title: 'Partie adverse',
        quality: dossier.opposing_procedural_role,
        main: names(['opposing_party']),
        counsel: names(['opposing_counsel']),
      },
    ];
  }, [links, dossier.client_procedural_role, dossier.opposing_procedural_role]);

  const memberName = (id: string) =>
    collaborators?.find((c) => c.id === id)?.full_name ??
    collaborators?.find((c) => c.id === id)?.email ?? '—';
  const memberAvatar = (id: string) =>
    collaborators?.find((c) => c.id === id)?.avatar_url ?? null;

  return (
    <aside className="w-[330px] flex-shrink-0 flex flex-col gap-3.5 overflow-y-auto">

      {/* Parties : nos clients face à la partie adverse, avec la qualité du camp */}
      <RailCard href={`/dossiers/${dossier.id}/parties`} ariaLabel="Ouvrir les parties">
        <RailTitle>Parties</RailTitle>
        {sides.every((side) => side.main.length + side.counsel.length === 0) ? (
          <p className="text-xs text-neutral-400">Aucune partie renseignée.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {sides.map((side) =>
              side.main.length + side.counsel.length === 0 ? null : (
                <div
                  key={side.key}
                  className={cn(
                    'rounded-lg border border-neutral-200 border-l-2 px-3 py-2 leading-tight',
                    side.key === 'ours' ? 'border-l-cime-primary' : 'border-l-[#c8431f]',
                  )}
                >
                  <small className="block text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">
                    {side.title}
                    {side.quality ? ` · ${side.quality}` : ''}
                  </small>
                  {side.main.map((name) => (
                    <b key={name} className="block truncate text-sm font-semibold text-cime-text-primary">
                      {name}
                    </b>
                  ))}
                  {side.counsel.length > 0 ? (
                    <span className="block truncate text-xs text-cime-text-muted">{side.counsel.join(', ')}</span>
                  ) : null}
                </div>
              ),
            )}
          </div>
        )}
      </RailCard>

      {/* Équipe */}
      <RailCard onClick={() => setMembersOpen(true)} ariaLabel="Ouvrir l’équipe">
        <RailTitle>
          Équipe
        </RailTitle>
        {orderedMembers.length === 0 && <p className="text-xs text-neutral-400">Aucun intervenant.</p>}
        <div className="flex flex-col gap-2">
          {orderedMembers.map((m) => (
            <div key={m.collaborator_id} className="flex items-center gap-2.5">
              {/* Le responsable garde un signal visuel : anneau doré autour
                  de sa photo (le fond plein n'a plus lieu d'être). */}
              <Avatar
                name={memberName(m.collaborator_id)}
                src={memberAvatar(m.collaborator_id)}
                size="xs"
                className={cn(
                  'w-7 h-7 text-[11px]',
                  m.role === 'lead_counsel' && 'ring-2 ring-cime-gold',
                )}
              />
              <div className="min-w-0 leading-tight">
                <span className="block text-[13px] font-semibold text-cime-text-primary truncate">
                  {memberName(m.collaborator_id)}
                </span>
                <span className="block text-[11px] text-neutral-400">
                  {m.role === 'lead_counsel' ? 'Responsable du dossier' : m.role === 'counsel' ? 'Collaborateur' : 'Lecture seule'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </RailCard>

      {/* Finances */}
      {canSeeBilling && (
        <RailCard href={`/dossiers/${dossier.id}/facturation`} ariaLabel="Ouvrir la facturation">
          <RailTitle>Finances</RailTitle>
          <div className="flex gap-2.5">
            <Kpi label="À facturer" value={euros(toBill)} />
            <Kpi label="Reste dû" value={euros(financial.outstandingTtc)} hot={financial.outstandingTtc > 0} />
          </div>
          <p className="mt-2 text-[11px] text-neutral-400">
            {formatDuration(uninvoiced.minutes)} de temps non facturé
          </p>
        </RailCard>
      )}

      {membersOpen && (
        <MembersModal onClose={() => setMembersOpen(false)}>
          <DossierMembersTab dossier={dossier} canManage={canManageMembers} />
        </MembersModal>
      )}
    </aside>
  );
}

// ─── Sous-composants ─────────────────────────────────────────────────────────

const RAIL_CARD_CLASS =
  'bg-white border border-neutral-200 rounded-cime shadow-sm px-4 py-3.5';
const RAIL_CARD_CLICKABLE =
  'block w-full text-left cursor-pointer hover:border-cime-primary/40 transition-colors';

function RailCard({
  children,
  className,
  href,
  onClick,
  ariaLabel,
}: {
  children: React.ReactNode;
  className?: string;
  href?: string;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  const classes = cn(RAIL_CARD_CLASS, (href || onClick) && RAIL_CARD_CLICKABLE, className);
  if (href) {
    return (
      <Link to={href} className={classes} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes} aria-label={ariaLabel}>
        {children}
      </button>
    );
  }
  return <div className={classes}>{children}</div>;
}

function RailTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400 mb-2.5">
      {children}
    </p>
  );
}

function Kpi({ label, value, hot }: { label: string; value: string; hot?: boolean }) {
  return (
    <div className="flex-1 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-0.5">{label}</span>
      <span className={cn('block text-base font-semibold tabular-nums', hot ? 'text-[#c8431f]' : 'text-cime-text-primary')}>
        {value}
      </span>
    </div>
  );
}

function MembersModal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-cime-modal>
      <div className="bg-white rounded-cime shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-200">
          <h2 className="text-base font-semibold text-cime-text-primary">Équipe du dossier</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
